import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  LiveKitRoom,
  PreJoin,
  VideoConference,
  RoomAudioRenderer,
  type LocalUserChoices,
} from "@livekit/components-react";
import { joinRoom, type JoinOut } from "../api";

type Phase = "prejoin" | "connecting" | "in-call" | "left" | "error";

export function Room() {
  const { code = "" } = useParams();
  const [search] = useSearchParams();
  const role = (search.get("role") === "owner" ? "owner" : "guest") as "owner" | "guest";

  const [phase, setPhase] = useState<Phase>("prejoin");
  const [choices, setChoices] = useState<LocalUserChoices | null>(null);
  const [session, setSession] = useState<JoinOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remembered = localStorage.getItem("video-app.displayName") || "";

  const onPreJoinSubmit = useCallback(
    async (values: LocalUserChoices) => {
      setChoices(values);
      setPhase("connecting");
      localStorage.setItem("video-app.displayName", values.username);
      try {
        const s = await joinRoom(code, values.username, role);
        setSession(s);
        setPhase("in-call");
      } catch (e) {
        setError(String(e));
        setPhase("error");
      }
    },
    [code, role],
  );

  const onDisconnected = useCallback(() => setPhase("left"), []);

  const connectOptions = useMemo(() => {
    const iceServers = (session?.ice_servers ?? [])
      .filter((s) => s.urls?.length)
      .map((s) => ({
        urls: s.urls,
        ...(s.username ? { username: s.username } : {}),
        ...(s.credential ? { credential: s.credential } : {}),
      }));
    return iceServers.length ? { rtcConfig: { iceServers } } : undefined;
  }, [session]);

  if (phase === "prejoin") {
    return (
      <div className="lk-prejoin-wrap">
        <PreJoin
          defaults={{ username: remembered }}
          onSubmit={onPreJoinSubmit}
          joinLabel="Подключиться"
          micLabel="Микрофон"
          camLabel="Камера"
          userLabel="Как вас представить?"
        />
      </div>
    );
  }

  if (phase === "connecting") {
    return <FullScreenMsg title="Подключаемся…" />;
  }

  if (phase === "error") {
    return (
      <FullScreenMsg
        title="Не удалось подключиться"
        subtitle={error ?? undefined}
        action={{ label: "Попробовать снова", onClick: () => setPhase("prejoin") }}
      />
    );
  }

  if (phase === "left") {
    return (
      <FullScreenMsg
        title="Вы вышли"
        subtitle="Запись каждой аудио- и видеодорожки уже собирается на сервере."
        action={{ label: "Зайти снова", onClick: () => setPhase("prejoin") }}
      />
    );
  }

  if (!session || !choices) return null;

  return (
    <div className="lk-room-wrap" data-lk-theme="default">
      <LiveKitRoom
        serverUrl={session.ws_url}
        token={session.token}
        connect
        connectOptions={connectOptions}
        video={choices.videoEnabled}
        audio={choices.audioEnabled}
        onDisconnected={onDisconnected}
        onError={(e) => {
          setError(String(e));
          setPhase("error");
        }}
      >
        <VideoConference />
        <RoomAudioRenderer />
      </LiveKitRoom>
    </div>
  );
}

function FullScreenMsg(props: {
  title: string;
  subtitle?: string;
  action?: { label: string; onClick: () => void };
}) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);
  return (
    <div className="page">
      <div className="card">
        <h1>{props.title}</h1>
        {props.subtitle && <p className="subtitle">{props.subtitle}</p>}
        {props.action && (
          <button className="btn primary" onClick={props.action.onClick}>
            {props.action.label}
          </button>
        )}
      </div>
    </div>
  );
}
